import type { Lang, LorsCopy } from "./translations";

export type June25Headline = { slug: string; headline: string; subhead: string };

// Meta-compliant headlines: educational framing, no personal attributes, no scare tactics,
// no implied assertions about the viewer's situation, finances, or family.
export const june25Headlines: June25Headline[] = [
  {
    slug: "estate-planning-basics-workshop",
    headline: "Estate Planning Basics: A Free Workshop in Santa Rosa.",
    subhead:
      "Join attorney Ruby Steinbrecher on June 25th for a conversation on wills, trusts, and probate.",
  },
  {
    slug: "learn-how-living-trusts-work",
    headline: "Learn How Living Trusts Work — Free Workshop in Santa Rosa.",
    subhead:
      "Free educational workshop on June 25th. A practical overview of how families in California use trusts to plan ahead.",
  },
  {
    slug: "wills-trusts-and-probate-explained",
    headline: "Wills, Trusts, and Probate — Explained in Plain English.",
    subhead:
      "Free in-person workshop, June 25th in Santa Rosa. An informative evening with attorney Ruby Steinbrecher.",
  },
  {
    slug: "how-california-probate-works",
    headline: "How California Probate Works (and How Estate Plans Help Avoid It).",
    subhead:
      "Free workshop on June 25th at the Epicenter in Santa Rosa. An educational session for anyone curious about estate planning.",
  },
  {
    slug: "free-estate-planning-workshop-june-25",
    headline: "Free Estate Planning Workshop — June 25th in Santa Rosa.",
    subhead:
      "An informative one-hour session at the Epicenter with attorney Ruby Steinbrecher. Bring your questions.",
  },
];

const baseEn = {
  eyebrow: "Free Estate Planning Workshop",
  headline: june25Headlines[0].headline,
  headlineBreakBefore: "",
  subhead: june25Headlines[0].subhead,
  subheadLabel: "A note from Ruby:",
  learnTitle: "What You'll Learn at This Workshop",
  learnItems: [
    "How wills, living trusts, and powers of attorney each work in California",
    "How a properly funded living trust can help a family pass assets without probate court",
    "What California's probate process generally looks like — timelines, court involvement, and typical costs",
    "Practical first steps for putting an estate plan in place, and the questions to ask before you do",
  ],
  quote:
    "Ruby Steinbrecher was fantastic. The family trust workshop was clear, informative, and extremely helpful for getting started with estate planning.",
  quoteAttribution: "Shawn H., Workshop Attendee",
  formDate: { short: "Thu, June 25th", long: "Thursday, June 25th" },
  formTime: "4:00–5:00 PM",
  formVenueLine1: "Epicenter — Trophy Room",
  formVenueLine2: "3215 Coffey Lane, Santa Rosa, CA 95403",
  formTitle: "Reserve Your Seat",
  formSubtitle: "Free to attend. Limited seating available.",
  formFirstName: "First name",
  formLastName: "Last name",
  formEmail: "Email",
  formPhone: "Phone",
  formSmsConsent:
    "I consent to receive text messages from the Law Office of Ruby Steinbrecher about this event, and occasional marketing messages. Message & data rates may apply. Text HELP for assistance, reply STOP to opt out.",
  formEnewsConsent:
    "I'd like to receive occasional email updates from the Law Office of Ruby Steinbrecher.",
  formPrivacy: "Privacy Policy",
  formSubmit: "Reserve My Seat",
  formSubmitting: "Reserving your seat…",
  formTrust: "",
  getDirections: "Get Directions",
  footerCopyright: "© 2026 Ruby Steinbrecher",
  footerPrivacy: "Privacy Policy",
  testimonialsTitle: "What Families Are Saying",
  aboutTitle: "Meet Ruby Steinbrecher",
  aboutRole: "Attorney",
  aboutBody: [
    "I have practiced law since 2007, first working at a medium-sized firm in San Francisco handling civil litigation, and then moving into estate planning, small business and contract law since 2010. I value people, the planet, and prosperity that does not adversely affect the former.",
    "Prior to entering the legal field, I had a variety of business and life experience that has only enhanced my ability to serve clients: Road Manager for Ken Kesey's bus trip to Great Britain in 1999, Human Resources at a tech startup in 2001, recognized for my work as a Case Manager at Casey Family Programs with disadvantaged youth in the foster care system in Oakland and the wider San Francisco-Bay Area in the early 2000's.",
    "Currently, I live in Sebastopol with my daughter, husband, and a menagerie of pets, where I enjoy gardening as it keeps me grounded and enjoying the beautiful weather.",
  ],
  registrationTitle: "Ready to Save Your Seat?",
  registrationSub: "Free to attend. Limited seating available.",
  registrationCta: "Save My Seat",
  registrationFoot:
    "No pressure. Just clear information so you can make an informed decision.",
  errorTitle: "Something went wrong",
  errorBody:
    "We couldn't reserve your seat. Please try again or call our office.",
  validation: {
    firstName: "First name is required",
    lastName: "Last name is required",
    email: "Enter a valid email",
    phone: "Enter a valid phone number",
  },
  timeAgo: {
    monthAgo: "a month ago",
    monthsAgo: (n: number) => `${n} months ago`,
    yearAgo: "a year ago",
  },
};

export const lorsContentJune25 = { en: baseEn, es: baseEn } as unknown as Record<Lang, LorsCopy>;

export const EPICENTER_DIRECTIONS_URL =
  "https://www.google.com/maps/dir/?api=1&destination=Epicenter,+3215+Coffey+Lane,+Santa+Rosa,+CA+95403";

export const getJune25Copy = (hParam: string | null): LorsCopy => {
  let idx = 0;
  if (hParam) {
    const slugIdx = june25Headlines.findIndex((h) => h.slug === hParam);
    if (slugIdx >= 0) {
      idx = slugIdx;
    } else {
      const n = Number(hParam);
      if (Number.isInteger(n) && n >= 1 && n <= june25Headlines.length) {
        idx = n - 1;
      }
    }
  }
  const pick = june25Headlines[idx];
  return { ...baseEn, headline: pick.headline, subhead: pick.subhead } as unknown as LorsCopy;
};
